import React, { useState, useEffect } from "react";
import GYAxios from "../../../util/GYAxios";
import moment from "moment-timezone";
import Noti from "../../../util/noti";
import styles from "../SiteCreationComp/AdminPreview.module.css";

const ACTION_LABELS = {
  approve: "Approved",
  unapprove: "Unapproved",
  email_sent: "Email Sent",
};

const ACTION_BADGE_CLASS = {
  approve: "label-success",
  unapprove: "label-warning",
  email_sent: "label-info",
};

const getApprovalRoleLabel = (before, after, actionType) => {
  if (actionType === "approve") {
    if (Number(before) === 1) return "By O&M";
    if (Number(before) === 2) return "By ACCT";
  } else if (actionType === "unapprove") {
    if (Number(after) === 1) return "By O&M";
    if (Number(after) === 2) return "By ACCT";
  }
  return ""
};

/**
 * Read-only modal that shows the approval / unapproval / email-sent history
 * for one invoice row and allows regenerating historical PDFs.
 *
 * Props:
 *   monthlyCalcId {number}  — id of the monthly_calc row
 *   siteName      {string}  — display label
 *   onClose       {func}    — called when the user closes the modal
 */
const VersionHistoryModal = ({ monthlyCalcId, siteName, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingPdf, setGeneratingPdf] = useState(null); // key = "<docType>-<logId>"

  useEffect(() => {
    if (!monthlyCalcId) return;
    GYAxios.get(`/invoice/pdf_version_log/${monthlyCalcId}`)
      .then((res) => {
        setLogs(Array.isArray(res.data) ? res.data : []);
        setLoading(false);
      })
      .catch(() => {
        Noti.create({ type: "e", msg: "Failed to load version history." });
        setLoading(false);
      });
  }, [monthlyCalcId]);

  const generatePdf = async (version_id, docType, action_type) => {
    const key = `${docType}-${version_id}-${action_type}`;
    setGeneratingPdf(key);
    try {
      const res = await GYAxios.post(
        `/invoice/pdf/from_version/${version_id}`,
        { doc_type: docType },
        { responseType: "blob", timeout: 60000 },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) {
      Noti.create({
        type: "e",
        msg:
          err.response?.data?.error ||
          "Failed to generate historical PDF. The data may not have been captured for this version.",
      });
    } finally {
      setGeneratingPdf(null);
    }
  };

  return (
    <div style={{ padding: "10px", maxHeight: "72vh", overflowY: "auto" }}>
      {/* Header */}
      <div style={{ marginBottom: "16px" }}>
        <h4 style={{ margin: 0 }}>
          Version History — <em>{siteName || "Invoice"}</em>
        </h4>
      </div>

      {/* Body */}
      {loading ? (
        <p style={{ color: "#888" }}>Loading...</p>
      ) : logs.length === 0 ? (
        <p style={{ color: "#888", minHeight: "500px", textAlign: "center", paddingTop: "150px", fontSize: "14px" }}>
          No version history found for this invoice. History is captured on
          approval, unapproval, and email-sending events.
        </p>
      ) : (
        <div style={{ overflowX: "auto", minHeight: "500px" }}>
          <table
            className={styles.compareStructureTable}
            style={{ width: "100%", fontSize: "12px", marginBottom: "10px", backgroundColor: "#fff" }}
          >
            <thead>
              <tr>
                <th style={{ width: "180px" }}>Action</th>
                <th>Performed By</th>
                <th style={{ whiteSpace: "nowrap" }}>Timestamp</th>
                <th>Invoice No</th>
                <th style={{ width: "150px", textAlign: "center" }}>Invoice PDF</th>
                <th style={{ width: "150px", textAlign: "center" }}>Perf. Report</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                // version_id is shared by all rows that belong to the same snapshot
                const invKey = `invoice-${log.version_id}-${log.action_type}`;
                const rptKey = `technical_report-${log.version_id}-${log.action_type}`;
                return (
                  <tr key={`${log.log_type}-${log.log_id}`}>
                    {/* Action */}
                    <td>
                      <span
                        className={`label ${ACTION_BADGE_CLASS[log.action_type] || "label-default"}`}
                        style={{ fontSize: "12px", padding: "6px 6px", minWidth: "80px", display: "flex", justifyContent: "center", alignItems: "center" }} 
                      >
                        {ACTION_LABELS[log.action_type] || log.action_type} {" "}
                        {log.approval_status_before != null && log.approval_status_after != null && log.action_type && (
                          getApprovalRoleLabel(log.approval_status_before, log.approval_status_after, log.action_type)
                        )}
                      </span>
                    </td>

                    {/* Performed By */}
                    <td style={{ wordBreak: "break-all" }}>
                      {log.performed_by_name || log.performed_by_email || "—"}
                    </td>

                    {/* Timestamp */}
                    <td style={{ whiteSpace: "nowrap" }}>
                      {log.created_at
                        ? moment(log.created_at).format("DD/MM/YYYY HH:mm")
                        : "—"}
                    </td>

                    {/* Invoice No */}
                    <td>{log.invoice_no || "—"}</td>

                    {/* Invoice PDF button */}
                    <td style={{ textAlign: "center" }}>
                      {Number(log.has_invoice) === 1 ? (
                        <button
                          className="btn btn-xs btn-primary"
                          disabled={!!generatingPdf}
                          onClick={() => generatePdf(log.version_id, "invoice", log.action_type)}
                        >
                          {generatingPdf === invKey ? "Generating…" : "Invoice PDF"}
                        </button>
                      ) : (
                        <span style={{ color: "#bbb" }}>—</span>
                      )}
                    </td>

                    {/* Performance Report PDF button */}
                    <td style={{ textAlign: "center" }}>
                      {Number(log.has_report) === 1 ? (
                        <button
                          className="btn btn-xs btn-info"
                          disabled={!!generatingPdf}
                          onClick={() => generatePdf(log.version_id, "technical_report", log.action_type)}
                        >
                          {generatingPdf === rptKey ? "Generating…" : "Report PDF"}
                        </button>
                      ) : (
                        <span style={{ color: "#bbb" }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* Footer */}
      <div style={{ marginTop: "8px" }}>
        <button className="btn btn-default closeBtn" onClick={onClose}>
          Close
        </button>
      </div>    
    </div>

  );
};

export default VersionHistoryModal;
